/**
 * Grocery / convenience store demo — full tour.
 *
 * Walks through: dashboard KPIs (sales, profit math, tax), critical alerts
 * (expiry + reorder), the rest of the dashboard (live feed, payment mix,
 * top sellers), tax monitor with CSV/PDF export, inventory (buy/sell/margin)
 * with bulk upload, then close. Uses fictional "Maple Grove Market" — no
 * real customer data is shown.
 */
export default {
  category: "grocery",
  title: "Run your grocery store on autopilot",
  initialRoute: "/maple-market/grocery?demo=1",

  steps: [
    {
      type: "narrate",
      text: "Hi there! Welcome to My Business Portal. Let me walk you through how it runs a grocery or convenience store, end to end.",
      duration: 11000,
    },
    {
      type: "point",
      target: '[data-testid="kpi-todays-sales-card"]',
      text: "Today's sales — everything your registers rang up, with a live comparison to yesterday so you spot the trend at a glance.",
      duration: 10500,
    },
    {
      type: "point",
      target: '[data-testid="kpi-profit-today-card"]',
      text: "And this is your real profit. We take today's sales, subtract what you paid for everything that sold — that's your cost of goods — and what's left is your actual take-home, with the margin percentage right next to it.",
      duration: 16000,
    },
    {
      type: "point",
      target: '[data-testid="kpi-tax-collected-card"]',
      text: "Tax collected. Every taxable item is taxed at your state rate automatically. Items like fresh produce and milk that are tax exempt aren't counted here — only what you actually owe the state.",
      duration: 14500,
    },
    {
      type: "point",
      target: '[data-testid="critical-alerts-banner"]',
      text: "Here's your daily attention list — products expiring within seven days, and items at or below their reorder threshold.",
      duration: 10000,
    },
    {
      type: "point",
      target: '[data-testid="expiry-alerts-card"]',
      text: "Each expiring item shows the exact days remaining so you can pull it, discount it, or push it before you lose the margin.",
      duration: 10000,
    },
    {
      type: "scroll",
      delta: 600,
      text: "Let's keep going.",
      duration: 3500,
    },
    {
      type: "narrate",
      text: "Your live transaction feed updates as each register checkout completes, hourly sales charted across the day, your payment mix split between card, cash, and EBT, and your top sellers so you know what to keep stocked.",
      duration: 16000,
    },
    {
      type: "navigate",
      url: "/maple-market/grocery?demo=1&tab=tax",
      text: "The Tax Monitor: every sale broken down — taxable, tax exempt, EBT — across any date range you pick.",
      duration: 11500,
    },
    {
      type: "narrate",
      text: "Export the full report as a CSV or a print-ready PDF in one click — ready for your accountant or the state portal.",
      duration: 10500,
    },
    {
      type: "navigate",
      url: "/maple-market/grocery?demo=1&tab=inventory",
      text: "Inventory: every product with its barcode, what you paid — that's your buy price — what you charge, the margin between them, current stock, and category.",
      duration: 14500,
    },
    {
      type: "narrate",
      text: "Adding products in bulk is easy — drop in a CSV, an Excel sheet, a Word document, or even a PDF price list from your supplier. The system reads the file and creates the products for you. Same flow works for your customer list.",
      duration: 15500,
    },
    {
      type: "navigate",
      url: "/maple-market/financials",
      text: "Financials has its own dedicated page — revenue trend, profit, margin — across any date range. And because this is the most sensitive screen in the whole app, you can lock it behind a four-digit privacy PIN with the toggle in the top right. Your cashiers can still ring up sales, but only you see the profit numbers.",
      duration: 22000,
    },
    {
      type: "navigate",
      url: "/maple-market/payments",
      text: "Payments has its own page too. Connect Stripe, Square, or PayPal with one click — customers can pay any invoice online with card, Apple Pay, or Google Pay, and Zelle is right there for the no-fee crowd.",
      duration: 17500,
    },
    {
      type: "navigate",
      url: "/maple-market/settings",
      text: "And here in settings: your business info, your logo — auto-generated by AI or upload your own with every previous logo saved in history — and your tax setup all live in one place.",
      duration: 16500,
    },
    {
      type: "narrate",
      text: "Plug in a Clover register and every sale, return, and inventory update flows in live.",
      duration: 9000,
    },
    {
      type: "narrate",
      text: "Free to start. No credit card required. Click Get Started up top and you're up and running in minutes.",
      duration: 9000,
    },
  ],
};
